import type { SceneDef } from "../../genesis/schema";

/**
 * Les Voyages Missionnaires de Paul — Scène 2 : Athènes, l'Aréopage.
 * Ancrage biblique : Actes 17:16-34 (discours 17:22-31). Parcours des idoles
 * dans la ville « pleine d'idoles » (Ac 17:16), découverte de l'autel
 * « À un dieu inconnu » (Ac 17:23), débat à choix avec les philosophes
 * épicuriens et stoïciens (npc_dialog, Ac 17:18), proclamation du Dieu
 * créateur à la tribune de l'Aréopage (lever_action, Ac 17:24-31),
 * conversion de Denys l'aréopagite et de Damaris (Ac 17:34).
 * Interactables « avançants » (pickup + drag_drop + lever_action +
 * guide_target) : 20
 *   = 9 pickups (4 fragments du verset Ac 17:28, 3 rouleaux des poètes
 *     grecs, 2 bornes du chemin des idoles)
 *   + 7 drag_drop (4 rouleaux des Écritures → Paul, 3 tablettes de
 *     questions → tribune de l'Aréopage)
 *   + 3 lever_action (discours de l'Aréopage, annonce à l'autel du dieu
 *     inconnu, entretien quotidien sur l'agora)
 *   + 1 guide_target (les curieux de l'agora vers l'Aréopage).
 * La difficulté D/A/E scale goalCount sur ce total réel (7 / 13 / 20).
 */
export const voyagesDePaulScene2: SceneDef = {
  id: "voyages-de-paul/scene-2",
  game: "voyages-de-paul",
  title: {
    fr: "Les Voyages Missionnaires de Paul — Athènes, l'Aréopage",
    en: "Paul's Missionary Journeys — Athens, the Areopagus",
  },
  order: 2,
  keyName: { fr: "Clé de la Délivrance", en: "Key of Deliverance" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 60,
    skyColor: "#aecbe8",
    groundColor: "#8f8368",
    lighting: "day",
    props: [
      // L'Acropole domine la ville au levant
      { id: "acropole", shape: "mountain", pos: [26, 0, -22], color: "#9a8f7c", size: 7, scatter: 4, scatterRadius: 10 },
      // Colonnade du Parthénon sur la hauteur
      { id: "parthenon_colonnes", shape: "cylinder", pos: [24, 0, -14], color: "#d8d0be", size: 2.6, scatter: 10, scatterRadius: 10 },
      // Le chemin des idoles : statues muettes le long de la voie ouest (Ac 17:16)
      { id: "statues_idoles", shape: "person", pos: [-18, 0, 8], color: "#8c8c94", size: 1.6, scatter: 10, scatterRadius: 14 },
      // Socles de marbre des statues
      { id: "socles_marbre", shape: "box", pos: [-20, 0, 4], color: "#c2bba8", size: 0.9, scatter: 10, scatterRadius: 13 },
      // La colline rocheuse de l'Aréopage, au nord
      { id: "colline_areopage", shape: "mountain", pos: [0, 0, -28], color: "#7d7466", size: 5, scatter: 5, scatterRadius: 14 },
      // Portiques de la stoa, où discutaient les philosophes
      { id: "stoa", shape: "box", pos: [16, 0, -4], color: "#b8ac90", size: 2.2, scatter: 8, scatterRadius: 12 },
      // Étals de l'agora, place publique du débat quotidien (Ac 17:17)
      { id: "etals_agora", shape: "box", pos: [20, 0, 8], color: "#9c7f52", size: 1.1, scatter: 9, scatterRadius: 9 },
      // Oliviers d'Attique
      { id: "oliviers", shape: "tree", pos: [-10, 0, 22], color: "#6d7a4a", size: 1.7, scatter: 12, scatterRadius: 14 },
      // Cyprès sombres autour des sanctuaires
      { id: "cypres", shape: "tree", pos: [12, 0, 20], color: "#3d5540", size: 2.1, scatter: 9, scatterRadius: 10 },
      // Amphores et jarres près des étals
      { id: "amphores", shape: "cylinder", pos: [22, 0, 14], color: "#a56f3e", size: 0.6, scatter: 8, scatterRadius: 7 },
      // Lauriers en fleurs le long des marches
      { id: "lauriers", shape: "sphere", pos: [0, 0, 22], color: "#c88fa2", size: 0.4, scatter: 16, scatterRadius: 16 },
      // Dalles de marbre clair du parvis
      { id: "dalles_parvis", shape: "box", pos: [0, 0, 4], color: "#cfc6ae", size: 0.8, scatter: 8, scatterRadius: 10 },
    ],
  },
  spawn: [0, 0, 12],
  difficulty: {
    // goalCount = interactables avançants accomplis (20 au total :
    // 9 pickups + 7 drag_drop + 3 lever_action + 1 guide_target).
    D: { goalCount: 7, hints: true },
    A: { goalCount: 13, hints: true },
    E: { goalCount: 20, hints: false, timerS: 420 },
  },
  interactables: [
    // ——— La tribune de l'Aréopage : mécanique signature (proclamer) ———
    {
      id: "discours_areopage",
      type: "lever_action",
      label: { fr: "Tribune de l'Aréopage — proclame le Dieu créateur (maintiens, Ac 17:24)", en: "Areopagus rostrum — proclaim the Creator God (hold, Acts 17:24)" },
      pos: [0, 0, -18],
      shape: "stele",
      color: "#e8c14a",
      size: 1.6,
      holdS: 4,
      count: 1,
    },
    {
      id: "autel_inconnu",
      type: "lever_action",
      label: { fr: "L'autel « À un dieu inconnu » — annonce Celui qu'ils révèrent sans le connaître (Ac 17:23)", en: "The altar 'TO AN UNKNOWN GOD' — announce the One they worship without knowing (Acts 17:23)" },
      pos: [0, 0, -4],
      shape: "stele",
      color: "#c9b98c",
      size: 1.4,
      holdS: 3,
      count: 1,
    },
    {
      id: "entretien_agora",
      type: "lever_action",
      label: { fr: "L'agora — comme Paul, entretiens-toi chaque jour avec ceux qui s'y trouvent (Ac 17:17)", en: "The agora — like Paul, reason daily with those who are there (Acts 17:17)" },
      pos: [14, 0, 6],
      shape: "person",
      color: "#d4b054",
      size: 1.3,
      holdS: 3,
      count: 1,
    },
    // ——— Le débat à choix : personnages nommés (npc_dialog) ———
    {
      id: "paul_athenes",
      type: "npc_dialog",
      label: { fr: "Paul : « Hommes Athéniens, je vous trouve à tous égards extrêmement religieux » (Ac 17:22)", en: "Paul: \"You men of Athens, I perceive that you are very religious in all things\" (Acts 17:22)" },
      pos: [0, 0, -14],
      shape: "person",
      color: "#e8c14a",
      size: 1.4,
    },
    {
      id: "philosophe_epicurien",
      type: "npc_dialog",
      label: { fr: "Le philosophe épicurien : « Que veut dire ce discoureur ? » — choisis ta réponse (Ac 17:18)", en: "The Epicurean philosopher: \"What does this babbler want to say?\" — choose your answer (Acts 17:18)" },
      pos: [3.5, 0, -17],
      shape: "person",
      color: "#8a9bb5",
      size: 1.35,
    },
    {
      id: "philosophe_stoicien",
      type: "npc_dialog",
      label: { fr: "Le philosophe stoïcien : « Il annonce des divinités étrangères » — réponds avec douceur (Ac 17:18)", en: "The Stoic philosopher: \"He seems to proclaim foreign deities\" — answer with gentleness (Acts 17:18)" },
      pos: [-3.5, 0, -17],
      shape: "person",
      color: "#7d8fa8",
      size: 1.35,
    },
    {
      id: "denys",
      type: "npc_dialog",
      label: { fr: "Denys l'aréopagite — il s'attacha à Paul et crut (Ac 17:34)", en: "Dionysius the Areopagite — he joined Paul and believed (Acts 17:34)" },
      pos: [4, 0, -21],
      shape: "person",
      color: "#6f8a6b",
      size: 1.35,
    },
    {
      id: "damaris",
      type: "npc_dialog",
      label: { fr: "Damaris — une femme d'Athènes qui crut ce jour-là (Ac 17:34)", en: "Damaris — a woman of Athens who believed that day (Acts 17:34)" },
      pos: [-4, 0, -21],
      shape: "person",
      color: "#a87a9b",
      size: 1.35,
    },
    // ——— Réparer l'accès au débat (build_slot) ———
    {
      id: "marches_areopage",
      type: "build_slot",
      label: { fr: "Les marches de l'Aréopage — replace les pierres pour monter à la tribune", en: "The Areopagus steps — set the stones back to climb to the rostrum" },
      pos: [0, 0, -10],
      shape: "box",
      color: "#b0a488",
      size: 1.8,
      targetId: "marches_areopage",
    },
    {
      id: "etal_discussion",
      type: "build_slot",
      label: { fr: "L'étal du débat sur l'agora — dresse la table où l'on discute chaque jour (Ac 17:17)", en: "The debating stall on the agora — set up the table where people reason daily (Acts 17:17)" },
      pos: [18, 0, 2],
      shape: "box",
      color: "#9c7f52",
      size: 1.4,
      targetId: "etal_discussion",
    },
    // ——— Rouleaux des Écritures : apporter la Parole à Paul (drag_drop) ———
    {
      id: "rouleau_esaie",
      type: "drag_drop",
      label: { fr: "Rouleau d'Ésaïe — porte-le à Paul pour annoncer le Créateur (És 42:5)", en: "Scroll of Isaiah — bring it to Paul to proclaim the Creator (Isa 42:5)" },
      pos: [8, 0, 10],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.6,
      targetId: "paul_athenes",
      count: 1,
    },
    {
      id: "rouleau_psaumes",
      type: "drag_drop",
      label: { fr: "Rouleau des Psaumes — « Les cieux racontent la gloire de Dieu » (Ps 19:2)", en: "Scroll of Psalms — \"The heavens declare the glory of God\" (Ps 19:1)" },
      pos: [-8, 0, 10],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.6,
      targetId: "paul_athenes",
      count: 1,
    },
    {
      id: "rouleau_genese",
      type: "drag_drop",
      label: { fr: "Rouleau de la Genèse — le Dieu qui a fait le monde et tout ce qui s'y trouve (Ac 17:24)", en: "Scroll of Genesis — the God who made the world and all things in it (Acts 17:24)" },
      pos: [11, 0, -3],
      shape: "cylinder",
      color: "#c2ad7c",
      size: 0.6,
      targetId: "paul_athenes",
      count: 1,
    },
    {
      id: "rouleau_prophetes",
      type: "drag_drop",
      label: { fr: "Rouleau des Prophètes — porte-le à Paul avant le discours", en: "Scroll of the Prophets — bring it to Paul before the address" },
      pos: [-11, 0, -3],
      shape: "cylinder",
      color: "#c2ad7c",
      size: 0.6,
      targetId: "paul_athenes",
      count: 1,
    },
    // ——— Tablettes de questions des philosophes → tribune (drag_drop) ———
    {
      id: "tablette_resurrection",
      type: "drag_drop",
      label: { fr: "Tablette de question : « Qu'est-ce que la résurrection ? » — porte-la à la tribune (Ac 17:18,32)", en: "Question tablet: \"What is the resurrection?\" — carry it to the rostrum (Acts 17:18,32)" },
      pos: [6, 0, -8],
      shape: "box",
      color: "#b8ac90",
      size: 0.6,
      targetId: "discours_areopage",
      count: 1,
    },
    {
      id: "tablette_dieu_inconnu",
      type: "drag_drop",
      label: { fr: "Tablette de question : « Qui est ce dieu inconnu ? » — porte-la à la tribune (Ac 17:23)", en: "Question tablet: \"Who is this unknown god?\" — carry it to the rostrum (Acts 17:23)" },
      pos: [-6, 0, -8],
      shape: "box",
      color: "#b8ac90",
      size: 0.6,
      targetId: "discours_areopage",
      count: 1,
    },
    {
      id: "tablette_jugement",
      type: "drag_drop",
      label: { fr: "Tablette de question : « Quel est ce jour fixé ? » — porte-la à la tribune (Ac 17:31)", en: "Question tablet: \"What is this appointed day?\" — carry it to the rostrum (Acts 17:31)" },
      pos: [9, 0, -13],
      shape: "box",
      color: "#aa9d80",
      size: 0.6,
      targetId: "discours_areopage",
      count: 1,
    },
    // ——— Fragments du verset Actes 17:28 (pickup) ———
    {
      id: "frag_vie",
      type: "pickup",
      label: { fr: "Fragment du verset : « En lui nous avons la vie » (Ac 17:28)", en: "Verse fragment: \"In him we live\" (Acts 17:28)" },
      pos: [-14, 0, 8],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_mouvement",
      type: "pickup",
      label: { fr: "Fragment du verset : « le mouvement » (Ac 17:28)", en: "Verse fragment: \"and move\" (Acts 17:28)" },
      pos: [-17, 0, 3],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_etre",
      type: "pickup",
      label: { fr: "Fragment du verset : « et l'être » (Ac 17:28)", en: "Verse fragment: \"and have our being\" (Acts 17:28)" },
      pos: [-20, 0, 12],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_race",
      type: "pickup",
      label: { fr: "Fragment du verset : « De lui nous sommes la race » (Ac 17:28)", en: "Verse fragment: \"For we are also his offspring\" (Acts 17:28)" },
      pos: [-15, 0, 16],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    // ——— Rouleaux des poètes grecs cités par Paul (pickup) ———
    {
      id: "poete_aratos",
      type: "pickup",
      label: { fr: "Rouleau du poète Aratos — « De lui nous sommes la race », cité par Paul (Ac 17:28)", en: "Scroll of the poet Aratus — \"For we are also his offspring\", quoted by Paul (Acts 17:28)" },
      pos: [20, 0, 12],
      shape: "cylinder",
      color: "#9fb4d4",
      size: 0.5,
    },
    {
      id: "poete_epimenide",
      type: "pickup",
      label: { fr: "Rouleau du poète Épiménide — « En lui nous avons la vie, le mouvement et l'être » (Ac 17:28)", en: "Scroll of the poet Epimenides — \"In him we live and move and have our being\" (Acts 17:28)" },
      pos: [23, 0, 7],
      shape: "cylinder",
      color: "#9fb4d4",
      size: 0.5,
    },
    {
      id: "poete_cleanthe",
      type: "pickup",
      label: { fr: "Rouleau du poète Cléanthe — Paul connaissait les poètes de ses auditeurs", en: "Scroll of the poet Cleanthes — Paul knew his hearers' poets" },
      pos: [17, 0, 16],
      shape: "cylinder",
      color: "#9fb4d4",
      size: 0.5,
    },
    // ——— Bornes du chemin des idoles (pickup) ———
    {
      id: "borne_idoles_1",
      type: "pickup",
      label: { fr: "Borne du chemin des idoles — la ville en était pleine, et Paul en fut ému (Ac 17:16)", en: "Marker on the idols' path — the city was full of them, and Paul was provoked (Acts 17:16)" },
      pos: [-8, 0, 18],
      shape: "sphere",
      color: "#d8d0be",
      size: 0.5,
    },
    {
      id: "borne_idoles_2",
      type: "pickup",
      label: { fr: "Seconde borne du parcours — des statues muettes au Dieu vivant", en: "Second marker of the course — from silent statues to the living God" },
      pos: [8, 0, 18],
      shape: "sphere",
      color: "#d8d0be",
      size: 0.5,
    },
    // ——— Les curieux de l'agora : conduits à l'Aréopage (guide_target) ———
    {
      id: "curieux_agora",
      type: "guide_target",
      label: { fr: "Les curieux de l'agora — conduis-les à l'Aréopage pour entendre Paul (Ac 17:19-21)", en: "The curious of the agora — lead them to the Areopagus to hear Paul (Acts 17:19-21)" },
      pos: [15, 0, -8],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "discours_areopage",
      count: 1,
    },
    // ——— Panneaux bibliques (stèles) ———
    {
      id: "panel_autel",
      type: "panel",
      label: { fr: "Lire : l'autel au dieu inconnu", en: "Read: the altar to the unknown god" },
      pos: [2.5, 0, -1],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_ac17_23",
    },
    {
      id: "panel_resurrection",
      type: "panel",
      label: { fr: "Sommet de l'Aréopage — le jour fixé et la preuve de la résurrection", en: "Areopagus summit — the appointed day and the proof of the resurrection" },
      pos: [0, 0, -24],
      shape: "stele",
      color: "#b9a06a",
      size: 1.5,
      panelId: "p_ac17_31",
    },
  ],
  objectives: [
    {
      id: "obj_discours",
      kind: "survive",
      label: { fr: "Proclame le Dieu créateur à la tribune de l'Aréopage (Ac 17:22-31)", en: "Proclaim the Creator God from the Areopagus rostrum (Acts 17:22-31)" },
      target: "discours_areopage",
    },
    {
      id: "obj_verset",
      kind: "collect",
      label: { fr: "Rassemble les quatre fragments du verset Actes 17:28", en: "Gather the four fragments of the verse Acts 17:28" },
      count: 4,
    },
  ],
  panels: [
    {
      id: "p_ac17_23",
      title: { fr: "À un dieu inconnu", en: "To an unknown god" },
      reference: "Actes 17:22-23",
      passage: {
        fr: "Paul, debout au milieu de l'Aréopage, dit: Hommes Athéniens, je vous trouve à tous égards extrêmement religieux. Car, en parcourant votre ville, et en considérant les objets de votre dévotion, j'ai même découvert un autel avec cette inscription: A un dieu inconnu! Ce que vous révérez sans le connaître, c'est ce que je vous annonce.",
        en: "Paul stood in the middle of the Areopagus and said, \"You men of Athens, I perceive that you are very religious in all things. For as I passed along and observed the objects of your worship, I found also an altar with this inscription: 'TO AN UNKNOWN GOD.' What therefore you worship in ignorance, I announce to you.",
      },
      explain: {
        fr: "Paul ne commence pas par condamner : il observe, il écoute, et il part de ce que les Athéniens cherchent déjà. Leur autel au dieu inconnu devient la porte d'entrée de l'Évangile. Annoncer le Dieu vivant, c'est souvent répondre à une soif qui existe déjà dans le cœur.",
        en: "Paul does not begin by condemning: he observes, listens, and starts from what the Athenians are already seeking. Their altar to the unknown god becomes the doorway for the Gospel. Proclaiming the living God often means answering a thirst already present in the heart.",
      },
      didYouKnow: {
        fr: "L'Aréopage (« colline d'Arès ») était à la fois un lieu et le plus ancien conseil d'Athènes. Paul y fut conduit parce que les Athéniens « ne passaient leur temps qu'à dire ou à écouter des nouvelles » (Ac 17:21).",
        en: "The Areopagus ('hill of Ares') was both a place and Athens' most ancient council. Paul was brought there because the Athenians 'spent their time in nothing else, but to tell or to hear some new thing' (Acts 17:21).",
      },
      application: {
        fr: "Quel « autel au dieu inconnu » — quelle question sincère — entends-tu autour de toi cette semaine ?",
        en: "What 'altar to an unknown god' — what sincere question — do you hear around you this week?",
      },
    },
    {
      id: "p_ac17_31",
      title: { fr: "Le jour fixé et la preuve donnée", en: "The appointed day and the assurance given" },
      reference: "Actes 17:30-31",
      passage: {
        fr: "Dieu, sans tenir compte des temps d'ignorance, annonce maintenant à tous les hommes, en tous lieux, qu'ils aient à se repentir, parce qu'il a fixé un jour où il jugera le monde selon la justice, par l'homme qu'il a désigné, ce dont il a donné à tous une preuve certaine en le ressuscitant des morts.",
        en: "The times of ignorance therefore God overlooked. But now he commands that all people everywhere should repent, because he has appointed a day in which he will judge the world in righteousness by the man whom he has ordained; of which he has given assurance to all men, in that he has raised him from the dead.",
      },
      explain: {
        fr: "Le sommet du discours n'est pas une idée mais un événement : la résurrection de Jésus, preuve donnée à tous. Certains se moquèrent, d'autres voulurent en entendre davantage, et quelques-uns crurent (Ac 17:32-34). La même Parole rencontre des cœurs différents — mais elle est annoncée à tous, avec espérance.",
        en: "The summit of the address is not an idea but an event: the resurrection of Jesus, an assurance given to all. Some mocked, others wanted to hear more, and a few believed (Acts 17:32-34). The same Word meets different hearts — yet it is announced to all, with hope.",
      },
      didYouKnow: {
        fr: "Parmi ceux qui crurent : Denys l'aréopagite, membre de ce conseil prestigieux, et une femme nommée Damaris (Ac 17:34). Même là où la moisson semble petite, Dieu se réserve des cœurs ouverts.",
        en: "Among those who believed: Dionysius the Areopagite, a member of that prestigious council, and a woman named Damaris (Acts 17:34). Even where the harvest seems small, God keeps hearts open for himself.",
      },
      application: {
        fr: "La résurrection change tout : quelle espérance concrète t'apporte-t-elle face à ce qui te semble « fixé » et sans issue ?",
        en: "The resurrection changes everything: what concrete hope does it give you facing what seems 'fixed' and hopeless?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_autel",
      level: "D",
      prompt: { fr: "Quelle inscription Paul découvrit-il sur un autel d'Athènes ?", en: "What inscription did Paul find on an altar in Athens?" },
      choices: [
        { fr: "« Au dieu de la mer »", en: "\"To the god of the sea\"" },
        { fr: "« À un dieu inconnu »", en: "\"TO AN UNKNOWN GOD\"" },
        { fr: "« Au dieu des voyageurs »", en: "\"To the god of travelers\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« J'ai même découvert un autel avec cette inscription: A un dieu inconnu! » (Actes 17:23) Paul partit de cet autel pour annoncer le Dieu vivant que les Athéniens cherchaient sans le connaître.",
        en: "\"I found also an altar with this inscription: 'TO AN UNKNOWN GOD.'\" (Acts 17:23) Paul started from that altar to proclaim the living God whom the Athenians sought without knowing him.",
      },
    },
    {
      id: "q_temples",
      level: "A",
      prompt: { fr: "Selon Paul à l'Aréopage, où le Dieu qui a fait le monde n'habite-t-il pas ?", en: "According to Paul at the Areopagus, where does the God who made the world not dwell?" },
      choices: [
        { fr: "Dans les cieux", en: "In the heavens" },
        { fr: "Dans des temples faits de main d'homme", en: "In temples made with hands" },
        { fr: "Au milieu de son peuple", en: "Among his people" },
        { fr: "Dans les lieux déserts", en: "In desert places" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Le Dieu qui a fait le monde et tout ce qui s'y trouve, étant le Seigneur du ciel et de la terre, n'habite point dans des temples faits de main d'homme. » (Actes 17:24) Il n'est pas servi comme s'il avait besoin de quoi que ce soit : c'est lui qui donne à tous la vie et la respiration (Ac 17:25).",
        en: "\"The God who made the world and all things in it, he, being Lord of heaven and earth, doesn't dwell in temples made with hands.\" (Acts 17:24) He is not served as though he needed anything: he himself gives to all life and breath (Acts 17:25).",
      },
    },
    {
      id: "q_qui_crut",
      level: "E",
      prompt: { fr: "Qui s'attacha à Paul et crut après le discours de l'Aréopage ?", en: "Who joined Paul and believed after the Areopagus address?" },
      choices: [
        { fr: "Un philosophe épicurien et un magistrat", en: "An Epicurean philosopher and a magistrate" },
        { fr: "Denys l'aréopagite et une femme nommée Damaris", en: "Dionysius the Areopagite and a woman named Damaris" },
        { fr: "Le gardien du temple et sa famille", en: "The temple keeper and his family" },
        { fr: "Personne ne crut ce jour-là", en: "No one believed that day" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Quelques-uns néanmoins s'attachèrent à lui et crurent, Denys l'aréopagite, une femme nommée Damaris, et d'autres avec eux. » (Actes 17:34) Même quand plusieurs se moquent (Ac 17:32), la Parole semée porte du fruit.",
        en: "\"But certain men joined with him and believed, among whom also was Dionysius the Areopagite, and a woman named Damaris, and others with them.\" (Acts 17:34) Even when many mock (Acts 17:32), the sown Word bears fruit.",
      },
    },
    {
      id: "qD1",
      level: "D",
      prompt: { fr: "Que ressentit Paul en parcourant Athènes ?", en: "What did Paul feel as he went through Athens?" },
      choices: [
        { fr: "Il admira la sagesse des statues", en: "He admired the wisdom of the statues" },
        { fr: "Son esprit s'irritait au-dedans de lui, à la vue de cette ville pleine d'idoles", en: "His spirit was provoked within him as he saw the city full of idols" },
        { fr: "Il voulut repartir aussitôt vers Corinthe", en: "He wanted to leave at once for Corinth" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Comme Paul les attendait à Athènes, il sentait au-dedans de lui son esprit s'irriter, à la vue de cette ville pleine d'idoles. » (Actes 17:16) Cette émotion ne le poussa pas au mépris, mais à l'annonce du Dieu vivant.",
        en: "\"Now while Paul waited for them at Athens, his spirit was provoked within him as he saw the city full of idols.\" (Acts 17:16) That emotion did not drive him to contempt, but to proclaiming the living God.",
      },
    },
    {
      id: "qD2",
      level: "D",
      prompt: { fr: "Quels philosophes se mirent à discuter avec Paul à Athènes ?", en: "Which philosophers began to debate with Paul in Athens?" },
      choices: [
        { fr: "Des pythagoriciens et des cyniques", en: "Pythagoreans and Cynics" },
        { fr: "Des platoniciens et des sophistes", en: "Platonists and Sophists" },
        { fr: "Des épicuriens et des stoïciens", en: "Epicureans and Stoics" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Quelques philosophes épicuriens et stoïciens se mirent à parler avec lui. » (Actes 17:18) Deux écoles rivales — le plaisir mesuré pour les uns, la maîtrise de soi pour les autres — et Paul leur annonce à tous Jésus et la résurrection.",
        en: "\"Some of the Epicurean and Stoic philosophers also encountered him.\" (Acts 17:18) Two rival schools — measured pleasure for one, self-mastery for the other — and Paul proclaims to them all Jesus and the resurrection.",
      },
    },
    {
      id: "qD3",
      level: "D",
      prompt: { fr: "Où Paul s'entretenait-il chaque jour avec ceux qu'il rencontrait ?", en: "Where did Paul reason daily with those he met?" },
      choices: [
        { fr: "Sur l'agora, la place publique", en: "In the agora, the marketplace" },
        { fr: "Dans le théâtre de la ville", en: "In the city theater" },
        { fr: "Sur les marches du Parthénon", en: "On the steps of the Parthenon" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il s'entretenait donc dans la synagogue avec les Juifs et les hommes craignant Dieu, et sur la place publique chaque jour avec ceux qu'il rencontrait. » (Actes 17:17) L'Évangile ne reste pas enfermé : il va là où les gens vivent et discutent.",
        en: "\"So he reasoned in the synagogue with the Jews and the devout persons, and in the marketplace every day with those who met him.\" (Acts 17:17) The Gospel does not stay shut in: it goes where people live and talk.",
      },
    },
    {
      id: "qD4",
      level: "D",
      prompt: { fr: "Quel surnom certains philosophes donnèrent-ils à Paul en l'entendant ?", en: "What name did some philosophers give Paul when they heard him?" },
      choices: [
        { fr: "« Le sage d'Orient »", en: "\"The sage from the East\"" },
        { fr: "« Ce discoureur »", en: "\"This babbler\"" },
        { fr: "« Le nouveau Socrate »", en: "\"The new Socrates\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Les uns disaient: Que veut dire ce discoureur? » (Actes 17:18) Le mot grec évoque un oiseau qui picore des graines : un ramasseur d'idées. Moqué, Paul n'en fut pas découragé — il monta à l'Aréopage annoncer le Dieu créateur.",
        en: "\"Some said, 'What does this babbler want to say?'\" (Acts 17:18) The Greek word pictures a bird pecking up seeds: a collector of scraps of ideas. Mocked, Paul was not discouraged — he went up to the Areopagus to proclaim the Creator God.",
      },
    },
    {
      id: "qA1",
      level: "A",
      prompt: { fr: "Comment réagirent les auditeurs quand Paul parla de la résurrection des morts ?", en: "How did the hearers react when Paul spoke of the resurrection of the dead?" },
      choices: [
        { fr: "Tous crurent aussitôt", en: "They all believed at once" },
        { fr: "Tous le chassèrent de la ville", en: "They all drove him out of the city" },
        { fr: "Les uns se moquèrent, d'autres voulurent l'entendre une autre fois, quelques-uns crurent", en: "Some mocked, others wanted to hear him again, a few believed" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Lorsqu'ils entendirent parler de résurrection des morts, les uns se moquèrent, et les autres dirent: Nous t'entendrons là-dessus une autre fois. » (Actes 17:32) Mais quelques-uns crurent, dont Denys et Damaris (Ac 17:34) : la même Parole rencontre des cœurs différents.",
        en: "\"Now when they heard of the resurrection of the dead, some mocked; but others said, 'We want to hear you again concerning this.'\" (Acts 17:32) Yet a few believed, among them Dionysius and Damaris (Acts 17:34): the same Word meets different hearts.",
      },
    },
    {
      id: "qA2",
      level: "A",
      prompt: { fr: "Selon Paul, pourquoi Dieu n'est-il point servi par des mains humaines ?", en: "According to Paul, why is God not served by human hands?" },
      choices: [
        { fr: "Parce qu'il n'a besoin de rien : c'est lui qui donne à tous la vie, la respiration et toutes choses", en: "Because he needs nothing: he himself gives to all life and breath and all things" },
        { fr: "Parce que seuls les prêtres peuvent le servir", en: "Because only priests may serve him" },
        { fr: "Parce que les sacrifices coûtent trop cher", en: "Because sacrifices cost too much" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il n'est point servi par des mains humaines, comme s'il avait besoin de quoi que ce soit, lui qui donne à tous la vie, la respiration, et toutes choses. » (Actes 17:25) Tout est renversé : ce n'est pas Dieu qui a besoin de nous, c'est nous qui recevons tout de lui.",
        en: "\"He isn't served by men's hands, as though he needed anything, seeing he himself gives to all life and breath and all things.\" (Acts 17:25) Everything is turned around: it is not God who needs us; it is we who receive everything from him.",
      },
    },
    {
      id: "qA3",
      level: "A",
      prompt: { fr: "Pourquoi les Athéniens conduisirent-ils Paul à l'Aréopage ?", en: "Why did the Athenians bring Paul to the Areopagus?" },
      choices: [
        { fr: "Pour le juger comme un criminel", en: "To try him as a criminal" },
        { fr: "Pour connaître cette nouvelle doctrine, eux qui aimaient dire ou écouter des nouveautés", en: "To know this new teaching, they who loved to tell or hear some new thing" },
        { fr: "Pour le nommer membre du conseil", en: "To make him a member of the council" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Pourrions-nous savoir quelle est cette nouvelle doctrine que tu enseignes ? … Or, tous les Athéniens… ne passaient leur temps qu'à dire ou à écouter des nouvelles. » (Actes 17:19-21) Leur curiosité devint la porte d'entrée de l'Évangile.",
        en: "\"May we know what this new teaching is, which you are speaking about? … Now all the Athenians… spent their time in nothing else, but either to tell or to hear some new thing.\" (Acts 17:19-21) Their curiosity became the doorway for the Gospel.",
      },
    },
    {
      id: "qA4",
      level: "A",
      prompt: { fr: "Qu'annonce Dieu maintenant à tous les hommes, selon le discours de l'Aréopage ?", en: "What does God now command all people, according to the Areopagus address?" },
      choices: [
        { fr: "De bâtir de plus beaux temples", en: "To build more beautiful temples" },
        { fr: "D'offrir davantage de sacrifices", en: "To offer more sacrifices" },
        { fr: "Qu'en tous lieux, ils aient à se repentir", en: "That everywhere they should repent" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Dieu, sans tenir compte des temps d'ignorance, annonce maintenant à tous les hommes, en tous lieux, qu'ils aient à se repentir. » (Actes 17:30) Ni pierres ni offrandes : Dieu demande un cœur qui se tourne vers lui.",
        en: "\"The times of ignorance therefore God overlooked. But now he commands that all people everywhere should repent.\" (Acts 17:30) Neither stones nor offerings: God asks for a heart that turns to him.",
      },
    },
    {
      id: "qE1",
      level: "E",
      prompt: { fr: "« De lui nous sommes la race » : d'où Paul tire-t-il cette parole citée à l'Aréopage ?", en: "\"For we are also his offspring\": where does Paul draw this saying quoted at the Areopagus?" },
      choices: [
        { fr: "Du prophète Ésaïe", en: "From the prophet Isaiah" },
        { fr: "De quelques-uns des poètes grecs, comme Aratos", en: "From some of the Greek poets, such as Aratus" },
        { fr: "D'un psaume de David", en: "From a psalm of David" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« …comme l'ont dit aussi quelques-uns de vos poètes: De lui nous sommes la race. » (Actes 17:28) Paul cite Aratos, un poète connu de ses auditeurs : il connaissait leur culture et s'en servit comme d'un pont vers la vérité de l'Évangile.",
        en: "\"…as some of your own poets have said, 'For we are also his offspring.'\" (Acts 17:28) Paul quotes Aratus, a poet his hearers knew: he understood their culture and used it as a bridge toward the truth of the Gospel.",
      },
    },
    {
      id: "qE2",
      level: "E",
      prompt: { fr: "Quelle « preuve certaine » Dieu a-t-il donnée à tous, selon Actes 17:31 ?", en: "What \"assurance\" has God given to all, according to Acts 17:31?" },
      choices: [
        { fr: "La résurrection d'entre les morts de l'homme qu'il a désigné pour juger le monde", en: "The resurrection from the dead of the man he has ordained to judge the world" },
        { fr: "La grandeur du temple de Jérusalem", en: "The greatness of the temple in Jerusalem" },
        { fr: "La sagesse des philosophes grecs", en: "The wisdom of the Greek philosophers" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« …il a fixé un jour où il jugera le monde selon la justice, par l'homme qu'il a désigné, ce dont il a donné à tous une preuve certaine en le ressuscitant des morts. » (Actes 17:31) Le sommet du discours n'est pas une idée mais un événement : la résurrection de Jésus.",
        en: "\"…he has appointed a day in which he will judge the world in righteousness by the man whom he has ordained; of which he has given assurance to all men, in that he has raised him from the dead.\" (Acts 17:31) The summit of the address is not an idea but an event: the resurrection of Jesus.",
      },
    },
    {
      id: "qE3",
      level: "E",
      prompt: { fr: "Selon Actes 17:26-27, pourquoi Dieu a-t-il déterminé les temps et les bornes des peuples ?", en: "According to Acts 17:26-27, why did God appoint the seasons and the boundaries of the peoples?" },
      choices: [
        { fr: "Pour séparer définitivement les nations", en: "To separate the nations for ever" },
        { fr: "Pour établir la puissance de Rome", en: "To establish the power of Rome" },
        { fr: "Pour que les hommes cherchent le Seigneur et le trouvent, lui qui n'est pas loin de chacun de nous", en: "So that people would seek the Lord and find him, though he is not far from each one of us" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« …il a voulu qu'ils cherchassent le Seigneur, et qu'ils s'efforçassent de le trouver en tâtonnant, bien qu'il ne soit pas loin de chacun de nous. » (Actes 17:26-27) L'histoire et la géographie mêmes sont ordonnées vers un but : la rencontre avec Dieu.",
        en: "\"…that they should seek the Lord, if perhaps they might reach out for him and find him, though he is not far from each one of us.\" (Acts 17:26-27) History and geography themselves are ordered toward one goal: meeting God.",
      },
    },
    {
      id: "qD5",
      level: "D",
      prompt: { fr: "Quels compagnons Paul attendait-il à Athènes ?", en: "Which companions was Paul waiting for in Athens?" },
      choices: [
        { fr: "Pierre et Jean", en: "Peter and John" },
        { fr: "Silas et Timothée", en: "Silas and Timothy" },
        { fr: "Barnabas et Marc", en: "Barnabas and Mark" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Ceux qui conduisaient Paul reçurent « l'ordre de dire à Silas et à Timothée de le rejoindre au plus tôt » (Actes 17:15). C'est en les attendant à Athènes que son esprit s'irrita à la vue des idoles (Ac 17:16) — l'attente devint mission.",
        en: "Those who escorted Paul received \"a commandment to Silas and Timothy that they should come to him very quickly\" (Acts 17:15). It was while waiting for them at Athens that his spirit was provoked by the idols (Acts 17:16) — the waiting became a mission.",
      },
    },
    {
      id: "qD6",
      level: "D",
      prompt: { fr: "De quelle ville Paul venait-il d'arriver quand il entra dans Athènes ?", en: "From which city had Paul just come when he arrived in Athens?" },
      choices: [
        { fr: "De Corinthe", en: "From Corinth" },
        { fr: "De Jérusalem", en: "From Jerusalem" },
        { fr: "De Bérée", en: "From Berea" },
      ],
      answerIndex: 2,
      explain: {
        fr: "Quand les Juifs de Thessalonique vinrent agiter la foule à Bérée, « les frères firent aussitôt partir Paul du côté de la mer », et ceux qui l'accompagnaient « le menèrent jusqu'à Athènes » (Actes 17:13-15). Chassé d'une ville, l'Évangile entre dans la suivante.",
        en: "When the Jews of Thessalonica came stirring up the crowds at Berea, \"the brothers immediately sent out Paul to go as far as to the sea,\" and his escorts \"brought him as far as Athens\" (Acts 17:13-15). Driven from one city, the Gospel enters the next.",
      },
    },
    {
      id: "qD7",
      level: "D",
      prompt: { fr: "Quelles deux « nouveautés » annoncées par Paul intriguaient les philosophes ?", en: "What two 'novelties' proclaimed by Paul intrigued the philosophers?" },
      choices: [
        { fr: "Jésus et la résurrection", en: "Jesus and the resurrection" },
        { fr: "La loi et les prophètes", en: "The law and the prophets" },
        { fr: "Le temple et le sabbat", en: "The temple and the Sabbath" },
      ],
      answerIndex: 0,
      explain: {
        fr: "Ils disaient : « Il semble qu'il annonce des divinités étrangères », « parce qu'il annonçait Jésus et la résurrection » (Actes 17:18). Le cœur du message de Paul n'a jamais changé : une personne, Jésus, et un événement, sa résurrection.",
        en: "They said, \"He seems to be advocating foreign deities,\" \"because he preached Jesus and the resurrection\" (Acts 17:18). The heart of Paul's message never changed: a person, Jesus, and an event, his resurrection.",
      },
    },
    {
      id: "qA5",
      level: "A",
      prompt: { fr: "Juste avant Athènes, que faisaient chaque jour les Juifs de Bérée, aux sentiments plus nobles ?", en: "Just before Athens, what did the more noble Jews of Berea do every day?" },
      choices: [
        { fr: "Ils offraient des sacrifices au temple", en: "They offered sacrifices at the temple" },
        { fr: "Ils examinaient les Écritures, pour vérifier ce qu'on leur disait", en: "They examined the Scriptures, to see whether these things were so" },
        { fr: "Ils jeûnaient jusqu'au soir", en: "They fasted until evening" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ces Juifs avaient des sentiments plus nobles… ils examinaient chaque jour les Écritures, pour voir si ce qu'on leur disait était exact. » (Actes 17:11) Recevoir la Parole avec empressement ET la vérifier : le modèle béréen reste celui de tout disciple.",
        en: "\"Now these were more noble… examining the Scriptures daily to see whether these things were so.\" (Acts 17:11) Receiving the Word with readiness of mind AND verifying it: the Berean model remains the pattern for every disciple.",
      },
    },
    {
      id: "qA6",
      level: "A",
      prompt: { fr: "Selon le discours de l'Aréopage, à partir de quoi Dieu a-t-il fait toutes les nations ?", en: "According to the Areopagus address, from what did God make every nation?" },
      choices: [
        { fr: "De quatre familles dispersées", en: "From four scattered families" },
        { fr: "Des cendres du déluge", en: "From the ashes of the flood" },
        { fr: "D'un seul sang, pour habiter toute la surface de la terre", en: "From one blood, to dwell on all the surface of the earth" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il a fait que tous les hommes, sortis d'un seul sang, habitassent sur toute la surface de la terre. » (Actes 17:26) Devant les fiers Athéniens, Paul proclame l'unité du genre humain : une seule origine, un seul Créateur — aucun peuple n'est supérieur à un autre.",
        en: "\"He made from one blood every nation of men to dwell on all the surface of the earth.\" (Acts 17:26) Before the proud Athenians, Paul proclaims the unity of humankind: one origin, one Creator — no people is superior to another.",
      },
    },
    {
      id: "qA7",
      level: "A",
      prompt: { fr: "Dans quelle ville Paul se rendit-il après avoir quitté Athènes ?", en: "To which city did Paul go after leaving Athens?" },
      choices: [
        { fr: "À Corinthe", en: "To Corinth" },
        { fr: "À Rome", en: "To Rome" },
        { fr: "À Éphèse", en: "To Ephesus" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Après cela, Paul partit d'Athènes, et se rendit à Corinthe. » (Actes 18:1) Là, il rencontra Aquilas et Priscille, travailla de ses mains comme faiseur de tentes, et y demeura un an et six mois, enseignant la parole de Dieu (Ac 18:2-3,11).",
        en: "\"After these things Paul departed from Athens and came to Corinth.\" (Acts 18:1) There he met Aquila and Priscilla, worked with his hands as a tentmaker, and stayed a year and six months, teaching the word of God (Acts 18:2-3,11).",
      },
    },
    {
      id: "qE4",
      level: "E",
      prompt: { fr: "Puisque nous sommes « la race de Dieu », que ne devons-nous pas penser, selon Paul ?", en: "Since we are 'God's offspring', what ought we not to think, according to Paul?" },
      choices: [
        { fr: "Que Dieu habite au-dessus des cieux", en: "That God dwells above the heavens" },
        { fr: "Que la divinité est semblable à de l'or, de l'argent ou de la pierre sculptés par l'art de l'homme", en: "That the Divine Nature is like gold, or silver, or stone, engraved by art of man" },
        { fr: "Que Dieu a fixé un jour de jugement", en: "That God has appointed a day of judgment" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ainsi donc, étant la race de Dieu, nous ne devons pas croire que la divinité soit semblable à de l'or, à de l'argent, ou à de la pierre, sculptés par l'art et l'industrie de l'homme. » (Actes 17:29) L'argument renverse l'idolâtrie : si nous sommes vivants et pensants, notre Créateur ne peut être une chose inerte façonnée par nos mains.",
        en: "\"Being then the offspring of God, we ought not to think that the Divine Nature is like gold, or silver, or stone, engraved by art and design of man.\" (Acts 17:29) The argument overturns idolatry: if we are living, thinking beings, our Creator cannot be a lifeless thing shaped by our own hands.",
      },
    },
    {
      id: "qE5",
      level: "E",
      prompt: { fr: "À Thessalonique, quelle accusation avait-on criée contre Paul et ses compagnons ?", en: "At Thessalonica, what accusation had been shouted against Paul and his companions?" },
      choices: [
        { fr: "« Ils pillent les temples de la ville »", en: "\"They are robbing the city's temples\"" },
        { fr: "« Ils refusent de payer l'impôt »", en: "\"They refuse to pay the tax\"" },
        { fr: "« Ces gens, qui ont bouleversé le monde… il y a un autre roi, Jésus »", en: "\"These who have turned the world upside down… there is another king, Jesus\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ces gens, qui ont bouleversé le monde, sont aussi venus ici… ils agissent contre les édits de César, disant qu'il y a un autre roi, Jésus. » (Actes 17:6-7) Accusation déformée, mais aveu magnifique : l'Évangile bouleversait réellement le monde — non par l'émeute, mais par la royauté de Jésus.",
        en: "\"These who have turned the world upside down have come here also… saying that there is another king, Jesus!\" (Acts 17:6-7) A twisted accusation, yet a magnificent admission: the Gospel really was turning the world upside down — not by riot, but by the kingship of Jesus.",
      },
    },
    {
      id: "qE6",
      level: "E",
      prompt: { fr: "Quelle démarche de Paul à Athènes peut inspirer notre témoignage aujourd'hui ?", en: "What approach of Paul in Athens can inspire our witness today?" },
      choices: [
        { fr: "Observer et écouter d'abord, puis partir de ce que les gens cherchent pour annoncer le Dieu vivant", en: "First observe and listen, then start from what people are seeking to proclaim the living God" },
        { fr: "Commencer par condamner publiquement les idoles de la ville", en: "Begin by publicly condemning the city's idols" },
        { fr: "Éviter tout contact avec la culture païenne", en: "Avoid all contact with pagan culture" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« En parcourant votre ville, et en considérant les objets de votre dévotion, j'ai même découvert un autel… Ce que vous révérez sans le connaître, c'est ce que je vous annonce. » (Actes 17:23) Paul observe, cite leurs poètes (Ac 17:28), et bâtit un pont de leur recherche vers le Dieu vivant — sans jamais diluer le message : repentance et résurrection (Ac 17:30-31).",
        en: "\"As I passed along and observed the objects of your worship, I found also an altar… What therefore you worship in ignorance, I announce to you.\" (Acts 17:23) Paul observes, quotes their poets (Acts 17:28), and builds a bridge from their seeking to the living God — without ever diluting the message: repentance and resurrection (Acts 17:30-31).",
      },
    },
    {
      id: "qD8",
      level: "D",
      prompt: { fr: "Avant Bérée et Athènes, pendant combien de sabbats Paul discuta-t-il dans la synagogue de Thessalonique ?", en: "Before Berea and Athens, for how many Sabbaths did Paul reason in the synagogue of Thessalonica?" },
      choices: [
        { fr: "Un seul sabbat", en: "One single Sabbath" },
        { fr: "Trois sabbats", en: "Three Sabbaths" },
        { fr: "Dix sabbats", en: "Ten Sabbaths" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Paul y entra, selon sa coutume. Pendant trois sabbats, il discuta avec eux, d'après les Écritures, expliquant et établissant que le Christ devait souffrir et ressusciter des morts. » (Actes 17:2-3) Sa méthode ne varie pas : ouvrir les Écritures et montrer Jésus — à Thessalonique comme plus tard à Athènes.",
        en: "\"Paul, as was his custom, went in to them, and for three Sabbath days reasoned with them from the Scriptures, explaining and demonstrating that the Christ had to suffer and rise again from the dead.\" (Acts 17:2-3) His method never changes: open the Scriptures and show Jesus — in Thessalonica as later in Athens.",
      },
    },
    {
      id: "qE7",
      level: "E",
      prompt: { fr: "À Thessalonique, chez quel hôte la foule en fureur chercha-t-elle Paul et Silas ?", en: "At Thessalonica, at whose house did the enraged crowd look for Paul and Silas?" },
      choices: [
        { fr: "Chez Jason, qui fut traîné devant les magistrats", en: "At Jason's house, and Jason was dragged before the rulers of the city" },
        { fr: "Chez Lydie, la marchande de pourpre", en: "At Lydia's house, the seller of purple" },
        { fr: "Chez Denys, membre de l'Aréopage", en: "At the house of Dionysius, member of the Areopagus" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ils se portèrent à la maison de Jason, et ils cherchèrent Paul et Silas… Ne les ayant pas trouvés, ils traînèrent Jason et quelques frères devant les magistrats de la ville. » (Actes 17:5-6) Jason dut verser une caution (Ac 17:9) : accueillir les messagers de l'Évangile avait un prix, et il le paya avec courage.",
        en: "\"Assaulting the house of Jason, they sought to bring them out to the people. When they didn't find them, they dragged Jason and certain brothers before the rulers of the city.\" (Acts 17:5-6) Jason had to post security (Acts 17:9): welcoming the Gospel's messengers carried a cost, and he paid it with courage.",
      },
    },
  ],
  tips: [
    {
      fr: "Suis d'abord le parcours des idoles à l'ouest : ramasse les bornes et les fragments du verset Actes 17:28 dispersés entre les statues muettes.",
      en: "First follow the idols' path to the west: gather the markers and the fragments of the verse Acts 17:28 scattered among the silent statues.",
    },
    {
      fr: "Recueille-toi à l'autel « À un dieu inconnu », puis porte les rouleaux des Écritures à Paul et les tablettes de questions à la tribune : le débat pourra commencer.",
      en: "Pause at the altar 'TO AN UNKNOWN GOD', then bring the Scripture scrolls to Paul and the question tablets to the rostrum: the debate can begin.",
    },
    {
      fr: "Parle aux philosophes épicurien et stoïcien, conduis les curieux de l'agora vers l'Aréopage, et maintiens le discours à la tribune jusqu'au bout (Ac 17:22-31).",
      en: "Speak with the Epicurean and Stoic philosophers, lead the curious of the agora to the Areopagus, and hold the address at the rostrum to the end (Acts 17:22-31).",
    },
  ],
  lesson: {
    fr: "Car en lui nous avons la vie, le mouvement, et l'être. (Actes 17:28)",
    en: "For in him we live, move, and have our being. (Acts 17:28)",
  },
  audio: { ambience: "crowd" },
  multiplayer: { mode: "coop", maxPlayers: 4 },
};

export default voyagesDePaulScene2;
